import React, { useState } from 'react'

import { useAxios } from '../../utilities/Requests/useAxios'

export type SubstanceObj = {
  id: number
  name: string
}

export type DosageUnitObj = {
  id: number
  unitName: string
}

export type DrugFormObj = {
  id: number
  name: string
}

export interface SubstanceWithUnitsDrugForms {
  substanceId: number
  substanceName: string
  availableUnits: Array<{
    dosageUnitId: number
    unitName: string
  }>
  availableDrugForms: Array<{
    drugFormId: number
    drugFormName: string
  }>
}

export type TrackedSubstanceObj = {
  id?: number
  patientId: number
  substanceId: number
  dosageUnitId: number
  drugFormId?: number
  isActive: boolean
  substance?: SubstanceObj
  dosageUnit?: DosageUnitObj
  drugForm?: DrugFormObj
  createdAt?: string
  modifiedAt?: string
}

export type SubstanceIntakeRecord = {
  trackedSubstanceId: number
  intakeAmount: number
  recordDate: string
  trackedSubstance?: TrackedSubstanceObj
}

export type PatientTaperRecordObj = {
  id: number
  recordDate: string
}

export type DiscontinueTrackedSubstanceRequest = {
  patientId: number
  trackedSubstanceId: number
}

export interface SubstanceCounterContextInterface {
  substancesWithUnitsDrugForms: SubstanceWithUnitsDrugForms[]
  getSubstancesWithUnitsDrugForms: () => Promise<any>
  createTrackedSubstance: (
    createTrackedSubstanceRequest: TrackedSubstanceObj
  ) => Promise<any>
  patientTrackedSubstances: TrackedSubstanceObj[]
  getPatientTrackedSubstancesForProvider: (PatientId: number) => Promise<any>
  discontinueTrackedSubstance: (
    request: DiscontinueTrackedSubstanceRequest
  ) => Promise<any>
  patientSubstanceIntakeRecords: SubstanceIntakeRecord[]
  getPatientSubstanceIntakeRecordsForProvider: (
    patientId: number
  ) => Promise<any>
}

export const SubstanceCounterContext =
  React.createContext<SubstanceCounterContextInterface | null>(null)

export const SubstanceCounterProvider = ({ children }: any) => {
  const { fetch } = useAxios()

  const [substancesWithUnitsDrugForms, setSubstancesWithUnitsDrugForms] =
    useState<SubstanceWithUnitsDrugForms[]>([])

  const [patientTrackedSubstances, setPatientTrackedSubstances] = useState<
    TrackedSubstanceObj[]
  >([])

  const [patientSubstanceIntakeRecords, setPatientSubstanceIntakeRecords] =
    useState<SubstanceIntakeRecord[]>([])

  const getSubstancesWithUnitsDrugForms = async () => {
    const { data, error } = await fetch({
      path: `SubstanceCounter/GetSubstancesWithUnitsDrugForms`,
    })
    if (data) {
      setSubstancesWithUnitsDrugForms(data)
      return data
    } else {
      console.warn(`GetSubstancesWithUnits-error: ${error}`)
    }
  }

  const createTrackedSubstance = async (
    createTrackedSubstanceRequest: TrackedSubstanceObj
  ) => {
    const { data, error } = await fetch({
      path: `SubstanceCounter/CreateTrackedSubstance`,
      methodType: 'POST',
      body: createTrackedSubstanceRequest,
    })
    if (data) {
      return data
    } else {
      console.warn(`createTrackedSubstance-error: ${error}`)
    }
  }

  const getPatientTrackedSubstancesForProvider = async (PatientId: number) => {
    const { data, error } = await fetch({
      path: `SubstanceCounter/GetPatientTrackedSubstancesForProvider?PatientId=${PatientId}`,
    })
    if (data) {
      setPatientTrackedSubstances(data)
      return data
    } else {
      console.warn(`GetPatientTrackedSubstancesForProvider-error: ${error}`)
    }
  }

  const discontinueTrackedSubstance = async (
    request: DiscontinueTrackedSubstanceRequest
  ) => {
    const { data, error } = await fetch({
      path: `SubstanceCounter/DiscontinueTrackedSubstance`,
      methodType: 'POST',
      body: request,
    })
    if (data) {
      return data
    } else {
      console.warn(`discontinueTrackedSubstance-error: ${error}`)
    }
  }

  const getPatientSubstanceIntakeRecordsForProvider = async (
    patientId: number
  ) => {
    const { data, error } = await fetch({
      path: `SubstanceCounter/GetSubstanceIntakeRecordsForProvider?PatientId=${patientId}`,
    })
    if (data) {
      setPatientSubstanceIntakeRecords(data)
      return data
    } else {
      console.warn(
        `GetPatientSubstanceIntakeRecordsForProvider-error: ${error}`
      )
    }
  }

  return (
    <SubstanceCounterContext.Provider
      value={{
        substancesWithUnitsDrugForms,
        getSubstancesWithUnitsDrugForms,
        createTrackedSubstance,
        patientTrackedSubstances,
        getPatientTrackedSubstancesForProvider,
        discontinueTrackedSubstance,
        patientSubstanceIntakeRecords,
        getPatientSubstanceIntakeRecordsForProvider,
      }}
    >
      {children}
    </SubstanceCounterContext.Provider>
  )
}
